import { useState } from "preact/hooks";

import { Modal } from "./Modal";
import { addCompany } from "../lib/api";
import { showToast } from "../store/appStore";

interface Props {
  open: boolean;
  onClose: () => void;
}

function resetForm(
  setQuery: (value: string) => void,
  setError: (value: string | null) => void,
  setSuccess: (value: string | null) => void,
  setNeedsReview: (value: boolean) => void,
) {
  setQuery("");
  setError(null);
  setSuccess(null);
  setNeedsReview(false);
}

/** Add Company modal — calls /api/add-company to auto-resolve ATS from a
 * name, domain, or careers URL. Reuses the existing backend route. */
export function AddCompanyModal({ open, onClose }: Props) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [needsReview, setNeedsReview] = useState(false);

  function handleClose() {
    resetForm(setQuery, setError, setSuccess, setNeedsReview);
    onClose();
  }

  async function submit() {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    setNeedsReview(false);
    try {
      const res = await addCompany(query.trim());
      setSuccess(res.message);
      setNeedsReview(res.needs_manual_resolution);
      setQuery("");
      if (res.needs_manual_resolution) {
        showToast("Saved for manual ATS review");
      } else {
        showToast(res.already_configured ? "Company already configured" : "Company added");
      }
      setTimeout(handleClose, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add company");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add Company Automatically"
      maxWidth="500px"
      footer={
        <>
          <button class="platform-btn" onClick={handleClose}>Cancel</button>
          <button class="btn-primary" onClick={submit} disabled={loading || !query.trim()}>
            {loading ? <span class="spinner" /> : null}
            <span>Add Company</span>
          </button>
        </>
      }
    >
      <div class="form-group">
        <label for="company-query-input">Company Name, Domain, or Careers URL</label>
        <p class="form-hint">
          Examples: <code>Stripe</code>, <code>vercel.com</code>, or{" "}
          <code>https://jobs.lever.co/vercel</code>
        </p>
        <input
          id="company-query-input"
          type="text"
          class="form-control"
          placeholder="e.g. Stripe"
          value={query}
          onInput={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
      </div>
      {loading && (
        <div class="form-loading">
          <span class="spinner" />
          <span>Searching job boards and resolving ATS configuration…</span>
        </div>
      )}
      {error && <div class="form-error">{error}</div>}
      {success && (
        <div class={needsReview ? "form-warning" : "form-success"}>{success}</div>
      )}
      {!loading && !success && (
        <p class="form-hint">
          If auto-detect works, the company is saved for your next scan. If not, it is still saved
          as <strong>unsupported</strong> and queued in Diagnostics for manual or AI-assisted review.
        </p>
      )}
    </Modal>
  );
}
